"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Clock, Mail, Phone, Wrench } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Touch() {
  const shouldReduceMotion = useReducedMotion();
  const animationProps = shouldReduceMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    animate: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 14
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.24,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  return <section id="get-in-touch" data-nav="dark" className="border-t border-border bg-background text-foreground py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div {...animationProps} className="border border-border bg-card p-6 sm:p-8 lg:p-10 rounded-sm relative overflow-hidden">
          {/* Subtle industrial grid ambient background accent */}
          <div className="absolute inset-0 bg-radial from-primary/5 via-transparent to-transparent pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Content Area */}
            <div className="lg:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-2 border border-border bg-muted/60 px-2.5 py-1 text-xs font-mono font-semibold tracking-wider text-muted-foreground uppercase rounded-sm">
                <Wrench className="size-3.5 text-primary" />
                <span>{"Technical Desk // Direct Channel"}</span>
              </div>

              <div className="border-l-3 border-primary pl-4">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold tracking-tight uppercase text-card-foreground">
                  {"Need equipment specs or workshop guidance?"}
                </h2>
                <p className="mt-2 text-sm sm:text-base text-muted-foreground font-sans">
                  {"Talk directly with our technical specialists for tool selections, bulk workshop provisioning, and nationwide shipping inquiries."}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <Link to="/ContactPage">
                  <Button className="bg-primary text-primary-foreground font-serif font-bold uppercase tracking-wider text-sm px-6 py-2.5 rounded-sm hover:bg-primary/90 transition-colors inline-flex items-center gap-2">
                    {"Open technical inquiry"}
                    <ArrowUpRight className="size-4" />
                  </Button>
                </Link>

                <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                  <Clock className="size-3.5 text-primary" />
                  <span>{"Mon - Fri: 8:00 AM - 5:00 PM PST"}</span>
                </div>
              </div>
            </div>

            {/* Right Contact Cards */}
            <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3.5">
              {/* Direct Phone Support Card */}
              <a href="tel:+14842918653" className="group flex items-start gap-4 p-4 border border-border bg-muted/30 hover:border-primary transition-[border-color,background-color] rounded-sm">
                <div className="p-2.5 bg-card border border-border group-hover:border-primary group-hover:text-primary transition-colors text-foreground rounded-sm shrink-0">
                  <Phone className="size-5" />
                </div>
                <div className="min-w-0">
                  <span className="block text-xs font-mono font-semibold text-muted-foreground uppercase tracking-wider">
                    {"Direct phone line"}
                  </span>
                  <span className="block text-sm sm:text-base font-mono font-bold text-foreground group-hover:text-primary transition-colors truncate">
                    {"+1 (484) 291-8653"}
                  </span>
                  <span className="block text-xs text-muted-foreground/80 mt-0.5">
                    {"Orange, CA dispatch office"}
                  </span>
                </div>
              </a>

              {/* Direct Email Inquiry Card */}
              <a href="mailto:HugaScalet@proton.me" className="group flex items-start gap-4 p-4 border border-border bg-muted/30 hover:border-primary transition-[border-color,background-color] rounded-sm">
                <div className="p-2.5 bg-card border border-border group-hover:border-primary group-hover:text-primary transition-colors text-foreground rounded-sm shrink-0">
                  <Mail className="size-5" />
                </div>
                <div className="min-w-0">
                  <span className="block text-xs font-mono font-semibold text-muted-foreground uppercase tracking-wider">
                    {"Technical inquiry desk"}
                  </span>
                  <span className="block text-sm sm:text-base font-mono font-bold text-foreground group-hover:text-primary transition-colors truncate">
                    {"HugaScalet@proton.me"}
                  </span>
                  <span className="block text-xs text-muted-foreground/80 mt-0.5">
                    {"Direct response within 24 hours"}
                  </span>
                </div>
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}